import { Button, Spin } from 'antd';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { isEmpty } from 'underscore';
import styles from '../../Style/AnalyticsPanel.module.scss';
import { ChartBox } from '../ChartBox';
import { IPlotVariable, IBestDataset } from '../../../../typings/analytics';
import { IAdvancedAnalyticsApiPayload } from '../../../../typings/chart';
import { IEnrichedMetadata } from '../../../../typings/metadata';
import { ADVANCED_ANALYTICS } from '../AnalyticsPanel';
import moment, { Moment } from 'moment';
import { DT0Format } from '../../../../generic/StaticVariable';
import { SATELLITE_TAB } from '../../../../generic/common';
import LayoutPanel from '../LayoutPanel';
import { StoreContext } from '../../../../store/store';

export default function AltimeterComparison(props: {
  datasets: IBestDataset[];
  enrichedMetadata: IEnrichedMetadata[];
  previewDatasetId: string;
  previewDatasetTimeRange?: [moment.Moment, moment.Moment];
  coordinates: number[];
  altimeterComparisonImage: string;
  altimeterComparisonLinks: string[];
  error: string;
  activePanel: string
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void;
  pointName: string;
  headerSection: any
  datasetChange: (item: string) => void
  onChangeActiveKey: (item: any) => void
}) {
  const {
    datasets,
    previewDatasetId,
    previewDatasetTimeRange,
    coordinates,
    enrichedMetadata,
    onUpdate,
    error,
    altimeterComparisonImage,
    pointName,
    activePanel,
    headerSection,
    datasetChange,
    onChangeActiveKey
  } = props;

  const [dataset, setDataset] = useState('');
  const [image, setImage] = useState('');
  const [variableX, setVariableX] = useState<IPlotVariable>();
  const [variablesX, setVariablesX] = useState<IPlotVariable[]>([]);
  const [updating, setUpdating] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [notSupported, setNotSupported] = useState(false);
  const [startDate, setStartDate] = useState(previewDatasetTimeRange?.[0]);
  const [endDate, setEndDate] = useState(previewDatasetTimeRange?.[1]);

  const limitationMessage =
    'Altimeter comparison is only available for wave height (Hm0) or wind speed (WS10)';
  const qualityDefinitionLabel = 'Definition of quality indices';
  const qualityDefinitionLink =
    'https://www.metocean-on-demand.com/fileshare/getfile.ashx?type=image&file_name=Definition_Of_Quality_Indices.PNG';
  const getAltimeterComparisonImage = useCallback(
    (
      datasetId: string,
      indexX: IPlotVariable,
      orderId: string,
      startDate: string,
      endDate: string
    ) => {
      const [longitude, latitude] = coordinates;
      setUpdating(true);
      if (indexX) {
        const payload: IAdvancedAnalyticsApiPayload = {
          PointName: pointName,
          DatasetId: datasetId,
          OrderId: orderId,
          OriginalOrderId: orderId,
          Longitude: longitude,
          Latitude: latitude,
          StartDate: startDate,
          EndDate: endDate,
          Title: indexX.Name,
          AcronymX: indexX.ShortName ? indexX.ShortName : indexX.Name,
          ColumnIndexX: indexX.Index,
          IntervalCountX: 16,
          AnalyticType: ADVANCED_ANALYTICS.ALTIMETER_COMPARISON,
        };
        onUpdate(payload);
      }
    },
    [coordinates, onUpdate]
  );

  useEffect(() => {
    if (previewDatasetId && !isEmpty(datasets) && !isEmpty(coordinates) && activePanel === SATELLITE_TAB && startDate && endDate) {
      setDataset(previewDatasetId);
      if (enrichedMetadata) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === previewDatasetId &&
            task.CoordinatesString === props.coordinates.join()
        );
        // Altimeter comparison is only available for wave height (Hm0) or wind speed (WS10))
        const supportedItems = ['Hm0', 'WS10'];
        if (task) {
          const itemsInfos = task.ItemsInfo.filter(
            (itemInfo: IPlotVariable) =>
              itemInfo.ShortName && supportedItems.includes(itemInfo.ShortName)
          );
          if (itemsInfos.length > 0) {
            setVariablesX(itemsInfos);
            const indexX = itemsInfos[0];
            setVariableX(indexX);
            if(altimeterComparisonImage === '' && !updating && error === '' ){
              getAltimeterComparisonImage(
                previewDatasetId,
                indexX,
                task.OrderId,
                startDate.format(DT0Format),
                endDate.format(DT0Format)
              );
            }
            setNotSupported(false);
          } else {
            setNotSupported(true);
          }
        }
      }
    }
  }, [
    datasets,
    previewDatasetId,
    coordinates,
    enrichedMetadata,
    startDate,
    endDate,
    activePanel,
    altimeterComparisonImage,
    updating,
    error
  ]);

  useEffect(() => {
    if (altimeterComparisonImage) {
      setUpdating(false);
      setImage(altimeterComparisonImage);
    }
  }, [altimeterComparisonImage]);

  useEffect(() => {
    if (error) {
      setUpdating(false);
      setHasError(true);
    } else {
      setHasError(false);
    }
  }, [error]);

  function doVariableChange(active: number | string) {
    const item = variablesX.find((a) => a.Index === active)!;
    setVariableX(item);
  }

  const doStartDateChange = (value: any) => {
    if (value) {
      setStartDate(value);
    }
  };

  const doEndDateChange = (value: any) => {
    if (value) {
      setEndDate(value);
    }
  };

  const onUpdateValidation = useCallback(() => {
    if (enrichedMetadata && variableX && dataset && startDate && endDate) {
      const task = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === dataset &&
          task.CoordinatesString === props.coordinates.join()
      );
      if (task) {
        getAltimeterComparisonImage(
          previewDatasetId,
          variableX,
          task.OrderId,
          startDate.format(DT0Format),
          endDate.format(DT0Format)
        );
      }
    }
  }, [enrichedMetadata, startDate, endDate, variableX]);

  function disabledStartDate(current: Moment) {
    if(notSupported) return true
    // Can not select date after end date
    if(endDate){
      const isAfterEndDate = current && current > endDate;
      // and not before preview dataset's time range and not after preview dataset's time range
      return isAfterEndDate
        ? isAfterEndDate
        : current.isBefore(props.previewDatasetTimeRange?.[0], 'day');
    }
    return true
  }

  function disabledEndDate(current: Moment) {
    if (!startDate || notSupported) {
      return true;
    }

    const isBeforeStartDate = current.isBefore(startDate, 'day');

    const isAfterPreviewRange = current.isAfter(props.previewDatasetTimeRange?.[1], 'day');

    return isBeforeStartDate || isAfterPreviewRange;
  }

  return (
    <LayoutPanel
      datasetOptions={datasets}
      endDate={endDate}
      startDate={startDate}
      disableStartDate={disabledStartDate}
      disableEndDate={disabledEndDate}
      disabledUpdateButton={notSupported}
      disabledVariable={notSupported}
      point={pointName}
      variable={variableX?.Index}
      variableOptions={variablesX}
      fieldColumnFields={[headerSection]}
      dataset={dataset}
      showCheckbox={false}
      onStartDateChange={doStartDateChange}
      onUpdate={onUpdateValidation}
      onPointChange={onChangeActiveKey}
      onDatasetChange={datasetChange}
      onVariableChange={doVariableChange}
      onEndDateChange={doEndDateChange}
      chartDefinition={(
        <Button type="link" href={qualityDefinitionLink}>
          {qualityDefinitionLabel}
        </Button>)
      }
      mainContent={
        notSupported ? (
          <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>{limitationMessage}</div>
        ) : (<>
        <ChartBox
          actionRow={<></>}
          key={'Altimeter Comparison'}
          title={'Altimeter Comparison'}
          height={updating || hasError ? '200px' : 'auto'}
          plot={
            updating ? (
              <div className={styles.spinner}>
                <Spin size="large" />
              </div>
            ) : hasError ? (
              <div>{error}</div>
            ) : (
              image && (
                <img
                  style={{
                    width: '80%',
                    objectFit: 'scale-down',
                    maxHeight: '54vh',
                    maxWidth: '100%',
                  }}
                  src={image}
                  alt="AltimeterComparison"
                />
              )
            )
          }

          dataset={''}
        />
        </>
      )}

    />
  );
}
